"use client";

import Image from "next/image";
import { memo, useEffect, useState } from "react";
import { useTranslation } from "next-i18next";
import { useContactModal } from "../context/ContactModalContext";
import { useRouter } from "next/router";
import dynamic from "next/dynamic";

const CalendlyForm = dynamic(() => import("./calendly-form"), {
  ssr: false,
  loading: () => (
    <div className="w-full rounded-2xl bg-white/10 backdrop-blur p-6 min-h-[280px] animate-pulse" />
  ),
});

const Banner = memo(function Banner() {
  const { t } = useTranslation(["home", "common"]);
  const router = useRouter();
  const lang = router.locale || "en";
  const { openModal } = useContactModal();

  const [showCalendly, setShowCalendly] = useState(false);

  useEffect(() => {
    // Prefer requestIdleCallback: waits until the main thread is actually
    // free (after LCP/hydration), instead of an arbitrary fixed delay.
    // Falls back to setTimeout for Safari, which doesn't support it.
    if ("requestIdleCallback" in window) {
      const id = window.requestIdleCallback(() => setShowCalendly(true), {
        timeout: 2500, // hard ceiling so it still loads even under heavy load
      });
      return () => window.cancelIdleCallback(id);
    } else {
      const id = window.setTimeout(() => setShowCalendly(true), 1500);
      return () => window.clearTimeout(id);
    }
  }, []);

  return (
    <section className="relative w-full aspect-[16/9] sm:aspect-auto sm:h-[800px] lg:h-[600px]">
      <Image
        src="/banners/homebanner.webp"
        alt="Prism Digital digital marketing agency in Dubai, UAE"
        fill
        priority
        quality={65}
        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 100vw, 1200px"
        className="object-cover object-top"
      />

      <div className="relative sm:absolute sm:inset-0 z-10 flex items-center">
        <div className="w-full max-w-[1600px] mx-auto px-6 sm:px-12 lg:px-20 py-10 sm:py-0">
          <div className="flex flex-col lg:flex-row items-start lg:items-center gap-8">
            <div className="w-full lg:w-[65%] flex flex-col gap-8 items-start">
              <h1 className="m-0 leading-[120%] font-bold text-[30px] sm:text-[40px] lg:text-[55px] text-white">
                {t("home:home.banner.title1")} <br />
                {t("home:home.banner.title2")}
              </h1>
              <p className="text-[16px] sm:text-[18px] lg:text-[20px] leading-[150%] m-0 text-white">
                {t("home:home.banner.content")}
              </p>
              <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4">
                {lang !== "ar" && (
                  <button
                    onClick={() => router.push("/free-seo-audit-tool")}
                    className="gemini-btn hidden sm:block w-fit text-[16px] text-white rounded-full px-6 py-3 font-medium"
                  >
                    {t("common:btnTxt.auditBtn")}
                  </button>
                )}
                <button
                  onClick={() => openModal({ trigger: "frame-component8" })}
                  className="hidden sm:block w-fit text-[16px] bg-white text-black rounded-full px-6 py-3 font-medium hover:opacity-70 transition"
                >
                  {t("common:btnTxt.getInTouch")}
                </button>
              </div>
            </div>

            {/* Right */}
            <div className="w-full lg:w-[40%]">
              <div className="min-h-[300px]">
                {showCalendly && <CalendlyForm />}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
});

export default Banner;
